/**
 * GENESYS Format - Versioned Point Data
 * 
 * Points are resolved ONLY from the exact selected version.
 * Cards not in the list default to 0 points.
 * Source: cardcluster.com/points-list/genesys (Official GENESYS point list)
 * 
 * Structure: genesysBanlists[version].cards[normalizedCardName] = points
 */

export const FORMAT_RULES = {
  name: 'GENESYS',
  defaultPointCap: 100,
  maxCopiesPerCard: 3,
  linkKeywords: ['Link'],
  pendulumKeywords: ['Pendulum'],
  usesAdvancedBanlist: false,
  defaultVersion: '2026.03',
  lastUpdated: '2026-03-12',
};

/**
 * Normalize card name for exact matching:
 * - trim whitespace
 * - normalize unicode quotes/apostrophes to ASCII
 * - collapse multiple spaces
 */
export function normalizeCardName(name) {
  if (!name) return '';
  return name
    .trim()
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")  // smart single quotes
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')   // smart double quotes
    .replace(/\u2013/g, '-')                         // en dash
    .replace(/\u2014/g, '-')                         // em dash
    .replace(/\s+/g, ' ');                           // collapse spaces
}

/**
 * Versioned GENESYS point lists.
 * Each version has its own complete point map.
 */
export const genesysBanlists = {
  '2026.03': {
    label: 'March 2026',
    effectiveDate: '2026-03-05',
    pointCap: 100,
    lastUpdated: '2026-03-12',
    cards: {
      // ═══ 100 points ═══
      'Angel O7': 100,
      'Arcana Force XXI - The World': 100,
      'Archlord Kristya': 100,
      'Archnemeses Eschatos': 100,
      'Archnemeses Protos': 100,
      'Artifact Scythe': 100,
      'Catapult Turtle': 100,
      'Contact "C"': 100,
      'Dark Eradicator Warlock': 100,
      'Djinn Releaser of Rituals': 100,
      'Fossil Dyna Pachycephalo': 100,
      'Ido the Supreme Magical Force': 100,
      'Jowgen the Spiritualist': 100,
      'Knightmare Corruptor Iblee': 100,
      'Level Eater': 100,
      "Majesty's Fiend": 100,
      'Moulinglacia the Elemental Lord': 100,
      'Royal Magical Library': 100,
      'Sillva, Warlord of Dark World': 100,
      'The Tyrant Neptune': 100,
      "Vanity's Fiend": 100,
      "Vanity's Ruler": 100,
      'Ame no Habakiri no Mitsurugi': 100,
      'Amorphactor Pain, the Imagination Dracoverlord': 100,
      'Evigishki Gustkraken': 100,
      'Magician of Black Chaos MAX': 100,
      'Dragon Master Magia': 100,
      'Invoked Caliga': 100,
      'Naturia Exterio': 100,
      'Red-Eyes Dark Dragoon': 100,
      'The Last Warrior from Another Planet': 100,
      'Beelze of the Diabolic Dragons': 100,
      'Junk Speeder': 100,
      'PSY-Framelord Omega': 100,
      'Ultimaya Tzolkin': 100,
      'Abyss Dweller': 100,
      'Beatrice, Lady of the Eternal': 100,
      'CXyz Gimmick Puppet Fanatix Machinix': 100,
      'Evilswarm Ouroboros': 100,
      'Number 16: Shock Master': 100,
      'Number 41: Bagooska the Terribly Tired Tapir': 100,
      'Number 43: Manipulator of Souls': 100,
      'Number 59: Crooked Cook': 100,
      'Number S0: Utopic ZEXAL': 100,
      'Outer Entity Azathot': 100,
      'Tellarknight Ptolemaeus': 100,
      'True King of All Calamities': 100,
      'Cold Wave': 100,
      'Confiscation': 100,
      'Delinquent Duo': 100,
      'Final Countdown': 100,
      'Heat Wave': 100,
      'Instant Fusion': 100,
      'Last Will': 100,
      'Original Sinful Spoils - Snake-Eye': 100,
      'Reversal Quiz': 100,
      'Sales Ban': 100,
      'The Forceful Sentry': 100,
      'Brain Research Lab': 100,
      'Mansion of the Dreadful Dolls': 100,
      'Mystic Mine': 100,
      'Sangen Summoning': 100,
      'Secret Village of the Spellcasters': 100,
      'Deck Lockdown': 100,
      'Gateway of the Six': 100,
      'Kaiser Colosseum': 100,
      'Mass Driver': 100,
      'Prohibition': 100,
      'Telekinetic Charging Cell': 100,
      'Dimensional Barrier': 100,
      'Ghost Meets Girl - A Masterful Mayakashi Shiranui Saga': 100,
      "Harpie's Feather Storm": 100,
      'Last Turn': 100,
      'Life Equalizer': 100,
      'Self-Destruct Button': 100,
      'And the Band Played On': 100,
      'Anti-Spell Fragrance': 100,
      "Dragon's Bind": 100,
      'Gozen Match': 100,
      'Imperial Order': 100,
      'Lose 1 Turn': 100,
      'Mind Drain': 100,
      'Mistake': 100,
      'Rivalry of Warlords': 100,
      'Royal Oppression': 100,
      'Skill Drain': 100,
      'Soul Drain': 100,
      'Summon Limit': 100,
      'There Can Be Only One': 100,
      "Vanity's Emptiness": 100,
      'Powersink Stone': 100,
      "Tyrant's Tirade": 100,
      'Zaborg the Mega Monarch': 100,

      // ═══ 97 points ═══
      'Kashtira Arise-Heart': 97,

      // ═══ 95 points ═══
      'Magical Scientist': 95,
      'Painful Choice': 95,

      // ═══ 94 points ═══
      'Trap Dustshoot': 94,

      // ═══ 93 points ═══
      'Triple Tactics Talent': 93,

      // ═══ 91 points ═══
      'Dinomorphia Rexterm': 91,
      'Elder Entity Norden': 91,
      'One for One': 91,
      'Give and Take': 91,

      // ═══ 88 points ═══
      'Infernity Launcher': 88,

      // ═══ 87 points ═══
      'Smoke Grenade of the Thief': 87,

      // ═══ 86 points ═══
      'Wind-Up Hunter': 86,

      // ═══ 85 points ═══
      'Fairy Tail - Snow': 85,
      'Number 1: Infection Buzzking': 85,
      'Number 89: Diablosis the Mind Hacker': 85,

      // ═══ 81 points ═══
      'Bahamut Shark': 81,

      // ═══ 80 points ═══
      'Gladiator Beast Tamer Editor': 80,
      'Baronne de Fleur': 80,
      'Crimson Dragon': 80,
      'Lavalval Chain': 80,
      'Number 39: Utopia Double': 80,
      'Number 97: Draglubion': 80,
      'Number 99: Utopia Dragonar': 80,
      "Raider's Knight": 80,

      // ═══ 77 points ═══
      'D/D/D Duo-Dawn King Kali Yuga': 77,

      // ═══ 76 points ═══
      'Phantom of Yubel': 76,

      // ═══ 75 points ═══
      "Koa'ki Meiru Drago": 75,
      'Magical Explosion': 75,

      // ═══ 70 points ═══
      'Barrier Statue of the Abyss': 70,
      'Barrier Statue of the Drought': 70,
      'Barrier Statue of the Heavens': 70,
      'Barrier Statue of the Inferno': 70,
      'Barrier Statue of the Stormwinds': 70,
      'Barrier Statue of the Torrent': 70,
      'Dodododo Warrior': 70,
      'Gimmick Puppet Nightmare': 70,
      'Blaze Fenix, the Burning Bombardment Bird': 70,
      'Masked HERO Dark Law': 70,
      'Number 75: Bamboozling Gossip Shadow': 70,
      'Numbers Eveil': 70,

      // ═══ 68 points ═══
      'Number 86: Heroic Champion - Rhongomyniad': 68,

      // ═══ 67 points ═══
      'Miscellaneousaurus': 67,
      'Thunder Dragon Colossus': 67,
      "Number 67: Pair-a-Dice Smasher": 67,

      // ═══ 66 points ═══
      'Gem-Knight Master Diamond': 66,
      'Zoodiac Broadbull': 66,
      'Branded Lost': 66,

      // ═══ 65 points ═══
      'Sixth Sense': 65,

      // ═══ 61 points ═══
      'Psi-Blocker': 61,

      // ═══ 60 points ═══
      'Ronintoadin': 60,
      'Substitoad': 60,
      'El Shaddoll Winda': 60,
      'Magical Mid-Breaker Field': 60,

      // ═══ 55 points ═══
      'Morphtronic Telefon': 55,

      // ═══ 51 points ═══
      'Earthbound Immortal Aslla piscu': 51,
      'Gem-Knight Lady Lapis Lazuli': 51,
      'Lunalight Liger Dancer': 51,
      'Black Garden': 51,
      'Mitsurugi Prayers': 51,
      'Mitsurugi Ritual': 51,
      'Banquet of Millions': 51,
      'D.D. Dynamite': 51,

      // ═══ 50 points ═══
      'Agido the Ancient Sentinel': 50,
      'Kelbek the Ancient Vanguard': 50,
      'Maxx "C"': 50,
      'Tearlaments Havnis': 50,
      'Tearlaments Merrli': 50,
      'Tearlaments Reinoheart': 50,
      'Tearlaments Scheiren': 50,
      'Tenpai Dragon Chundra': 50,
      'Wandering Gryphon Rider': 50,
      'Zoodiac Ratpier': 50,
      'Tearlaments Kitkallos': 50,
      'Chaos Ruler, the Chaotic Magical Dragon': 50,
      'Herald of the Arc Light': 50,
      'Naturia Beast': 50,
      'Number 40: Gimmick Puppet of Strings': 50,
      'Number 95: Galaxy-Eyes Dark Matter Dragon': 50,
      'Number C40: Gimmick Puppet of Dark Strings': 50,
      'Pilgrim Reaper': 50,
      'The Zombie Vampire': 50,
      'Monster Gate': 50,
      'Reasoning': 50,
      'Soul Charge': 50,
      'That Grass Looks Greener': 50,
      'Domain of the True Monarchs': 50,
      'Primeval Planet Perlereino': 50,
      'Chain Strike': 50,
      'Sangen Kaimen': 50,
      'Appointer of the Red Lotus': 50,
      'The Monarchs Erupt': 50,
      'Red Reboot': 50,

      // ═══ 40 points ═══
      'Atlantean Dragoons': 40,
      'Lady Labrynth of the Silver Castle': 40,
      'Cyber Angel Benten': 40,
      'Branded Fusion': 40,
      'Card Destruction': 40,
      'Card of Demise': 40,
      'Chaos Space': 40,
      'Dimension Fusion': 40,
      'Giant Trunade': 40,
      'Graceful Charity': 40,
      'Obedience Schooled': 40,
      'Pot of Prosperity': 40,
      'Necrovalley': 40,
      'Deception of the Sinful Spoils': 40,
      'The Fallen & The Virtuous': 40,
      'Return from the Different Dimension': 40,

      // ═══ 35 points ═══
      'Fire Formation - Tenki': 35,
      'Emergency Teleport': 35,

      // ═══ 33 points ═══
      'Ancient Gear Statue': 33,
      'Block Dragon': 33,
      'Bystial Magnamhut': 33,
      'Cornfield Coatl': 33,
      'Cyber Jar': 33,
      'Diviner of the Herald': 33,
      'Drytron Alpha Thuban': 33,
      'Eclipse Wyvern': 33,
      'Fishborg Blaster': 33,
      'Floowandereeze & Robina': 33,
      'Imsety, Glory of Horus': 33,
      'K9-66a Jokul': 33,
      'Lonefire Blossom': 33,
      'Master Peace, the True Dracoslaying King': 33,
      'Morphing Jar': 33,
      'Neptabyss, the Atlantean Prince': 33,
      'Red-Eyes Black Fullmetal Dragon': 33,
      'Schwarzschild Infinity Dragon': 33,
      'Sengenjin Wakes from a Millennium': 33,
      'Snoww, Unlight of Dark World': 33,
      'Starliege Seyfert': 33,
      'Swap Frog': 33,
      'Vanquish Soul Hollie Sue': 33,
      'Ohime the Manifested Mikanko': 33,
      'Albion the Sanctifire Dragon': 33,
      'Azamina Mu Rcielago': 33,
      'Guardian Chimera': 33,
      'Mementomictlan Tecuhtlica - Creation King': 33,
      'Mementotlan Twin Dragon': 33,
      'Centur-Ion Auxila': 33,
      'Shooting Riser Dragon': 33,
      'T.G. Hyper Librarian': 33,
      'Drytron Mu Beta Fafnir': 33,
      'Expurrely Noir': 33,
      'Gallant Granite': 33,
      'Infernal Flame Banshee': 33,
      'King of the Feral Imps': 33,
      'M-X-Saber Invoker': 33,
      'Super Quantal Mech King Great Magnus': 33,
      'Bonfire': 33,
      'Deep Sea Aria': 33,
      'Foolish Burial': 33,
      'Fossil Dig': 33,
      'Frightfur Patchwork': 33,
      'Fusion Destiny': 33,
      'Onomatopaira': 33,
      'Reinforcement of the Army': 33,
      "Shien's Smoke Signal": 33,
      'Terraforming': 33,
      'Wishes for Eyes of Blue': 33,
      'Floowandereeze and the Magnificent Map': 33,
      'Numeron Network': 33,
      'Pressured Planet Wraitsoth': 33,
      'Witch of the White Forest': 33,
      'Ancient Gear Advance': 33,
      'Brilliant Fusion': 33,
      'Card of Safe Return': 33,
      "King's Sarcophagus": 33,
      'Megalith Anastasis': 33,
      'Primite Lordly Lode': 33,
      'Zoodiac Barrage': 33,
      'EMERGENCY!': 33,
      'Floowandereeze and the Advent of Adventure': 33,
      'Mementotlan Bone Party': 33,
      'Quick Launch': 33,
      'Set Rotation': 33,
      'WANTED: Seeker of Sinful Spoils': 33,
      'Branded Expulsion': 33,

      // ═══ 30 points ═══
      'Bystial Baldrake': 30,
      'Bystial Druiswurm': 30,
      'Dracotail Faimena': 30,
      'Fallen of the White Dragon': 30,
      'Fiber Jar': 30,
      'Kashtira Fenrir': 30,
      'Kashtira Unicorn': 30,
      'The Bystial Lubellion': 30,
      'K9-17 "Ripper"': 30,
      'Numeron Calling': 30,
      'Pot of Greed': 30,

      // ═══ 27 points ═══
      'Cyber-Stein': 27,

      // ═══ 25 points ═══
      'Ext Ryzeal': 25,
      'Tenpai Dragon Genroku': 25,
      'Diabell, Queen of the White Forest': 25,
      'Charge of the Light Brigade': 25,
      'The Melody of Awakening Dragon': 25,
      'Nightmare Throne': 25,
      'Crystron Inclusion': 25,

      // ═══ 22 points ═══
      'Elzette, Azamina of the White Forest': 22,

      // ═══ 21 points ═══
      'Glow-Up Bulb': 21,
      'Cosmic Blazar Dragon': 21,
      'Hot Red Dragon Archfiend King Calamity': 21,
      'Stardust Sifr Divine Dragon': 21,
      'Number 100: Numeron Dragon': 21,

      // ═══ 20 points ═══
      'Ash Blossom & Joyous Spring': 20,
      'Bystial Saronir': 20,
      'Denko Sekka': 20,
      'Destiny HERO - Plasma': 20,
      'Diabellstar the Black Witch': 20,
      'Droll & Lock Bird': 20,
      'Ice Ryzeal': 20,
      'Inspector Boarder': 20,
      'K9-17 Izuna': 20,
      'Nightmare Apprentice': 20,
      'Spell Canceller': 20,
      'Sword Ryzeal': 20,
      'Therion "King" Regulus': 20,
      'Thunder King Rai-Oh': 20,
      'Azamina Ilia Silvia': 20,
      'Destiny HERO - Destroyer Phoenix Enforcer': 20,
      'Dracotail Arthalion': 20,
      'Light and Darkness Dragonlord': 20,
      'The Unstoppable Exodia Incarnate': 20,
      'Adamancipator Risen - Dragite': 20,
      'Blackwing - Boreastorm the Wicked Wind': 20,
      'Chaos Angel': 20,
      'Denglong, First of the Yang Zing': 20,
      'F.A. Dawn Dragster': 20,
      'Hot Red Dragon Archfiend Abyss': 20,
      'Psychic End Punisher': 20,
      'Swordsoul Grandmaster - Chixiao': 20,
      'Virtual World Kyubi - Shenshen': 20,
      'Cyber Dragon Infinity': 20,
      'D/D/D Wave High King Caesar': 20,
      'Divine Arsenal AA-ZEUS - Sky Thunder': 20,
      'Evolzar Lars': 20,
      'Number 38: Hope Harbinger Dragon Titanic Galaxy': 20,
      'Number F0: Utopic Draco Future': 20,
      'Primathmech Alembertian': 20,
      'Ryzeal Detonator': 20,
      'Ryzeal Duo Drive': 20,
      'Toadally Awesome': 20,
      'Traptrix Rafflesia': 20,
      'Varudras, the Final Bringer of the End Times': 20,
      'Zoodiac Drident': 20,
      'Goblin Biker Grand Entrance': 20,
      'Lyrilusc - Bird Call': 20,
      'Nadir Servant': 20,
      'Pot of Desires': 20,
      '"A Case for K9"': 20,
      'Called by the Grave': 20,
      'Crossout Designator': 20,
      'Big Welcome Labrynth': 20,
      'Dominus Impulse': 20,
      'Welcome Labrynth': 20,

      // ═══ 18 points ═══
      'Fire King Courtier Ulcanix': 18,
      'Uzuhime the Manifested Mikanko': 18,
      'Mikanko Shinbu - Noble Twins': 18,

      // ═══ 16 points ═══
      'Masquerade the Blazing Dragon': 16,

      // ═══ 15 points ═══
      'Elfnote Power Patron': 15,
      'K9-04 Noroi': 15,
      "Magicians' Souls": 15,
      'Mathmech Circular': 15,
      'PSY-Framegear Gamma': 15,
      'Spenta, the Magistus Sealer': 15,
      'Artmage Non-Finito': 15,
      'Dark Magician of Destruction': 15,
      'P.U.N.K. JAM Dragon Drive': 15,
      'Gagagaga Girl': 15,
      'Galaxy Photon Dragon': 15,
      'Gigantic Spright': 15,
      'Goblin Biker Big Gabonga': 15,
      'N.As.H. Knight': 15,
      'Wind-Up Carrier Zenmaity': 15,
      'Ketu Dracotail': 15,
      'Dragonic Diagram': 15,
      'Union Hangar': 15,
      'Barrier of the Voiceless Voice': 15,

      // ═══ 13 points ═══
      'Blackwing - Zephyros the Elite': 13,
      'Chaofeng, Phantom of the Yang Zing': 13,
      'Trishula, Dragon of the Ice Barrier': 13,
      'Phantom Fortress Enterblathnir': 13,
      'Triple Tactics Thrust': 13,
      'Super Polymerization': 13,

      // ═══ 12 points ═══
      'Lightning Storm': 12,
      'Infinite Impermanence': 12,

      // ═══ 11 points ═══
      'Vanquish Soul Jiaolong': 11,
      'Vanquish Soul Razen': 11,
      'One Day of Peace': 11,
      'Question': 11,

      // ═══ 10 points ═══
      'Dimension Shifter': 10,
      'Legendary Fire King Ponix': 10,
      'Lightsworn Dragonling': 10,
      'Purrely': 10,
      'Regenesis Archfiend': 10,
      'Rescue-ACE Preventer': 10,
      'Ritual Beast Tamer Elder': 10,
      'Shiina, Twin Tempests of Celestial Thunder': 10,
      'Spiritual Beast Tamer Lara': 10,
      'Dragonmaid Sheou': 10,
      'El Shaddoll Apkallone': 10,
      'Filia Regis': 10,
      "Lady's Dragonmaid": 10,
      'Lubellion the Searing Dragon': 10,
      'Mirrorjade the Iceblade Dragon': 10,
      'Bystial Dis Pater': 10,
      'Legendary Lord Six Samurai - Shi En': 10,
      'Legendary Six Samurai - Shi En': 10,
      'Naturia Barkion': 10,
      'Zalen the Shackled Dragon': 10,
      'Exosister Mikailis': 10,
      'K9-X "Ripper/M"': 10,
      'K9-X "Werewolf"': 10,
      'Mercurium the Living Quicksilver': 10,
      'Number 1: Numeron Gate Ekam': 10,
      'Number 2: Numeron Gate Dve': 10,
      'Number 3: Cicada King': 10,
      'Number 3: Numeron Gate Trini': 10,
      'Number 4: Numeron Gate Catvari': 10,
      'Number 60: Dugares the Timeless': 10,
      'Number 90: Galaxy-Eyes Photon Lord': 10,
      'Number C1: Numeron Chaos Gate Sunya': 10,
      'Super Starslayer TY-PHON - Sky Crisis': 10,
      'Totem Bird': 10,
      'Gold Sarcophagus': 10,
      'Interrupted Kaiju Slumber': 10,
      'Pot of Extravagance': 10,
      'Pre-Preparation of Rites': 10,
      'Rahu Dracotail': 10,
      'Regenesis': 10,
      'Swordsoul Emergence': 10,
      'Forbidden Crown': 10,
      'Forbidden Droplet': 10,
      'Kewl Tune Synchro': 10,
      'Mistaken Arrest': 10,
      'Purrely Sleepy Memory': 10,
      'Radiant Typhoon Chant': 10,
      'Radiant Typhoon Vision': 10,
      'Regenesis Code': 10,
      'Runick Tip': 10,
      'Spright Starter': 10,
      'Mikanko Water Arabesque': 10,
      'Different Dimension Ground': 10,
      'Dominus Purge': 10,
      'Grisaille Prison': 10,
      'Songs of the Dominators': 10,
      'The Black Goat Laughs': 10,
      'Royal Decree': 10,
      'Shaddoll Schism': 10,

      // ═══ 9 points ═══
      'Mulcharmy Purulia': 9,
      "Harpie's Feather Duster": 9,

      // ═══ 8 points ═══
      'Effect Veiler': 8,

      // ═══ 7 points ═══
      'Danger! Nessie!': 7,
      'Danger!? Jackalope?': 7,
      'Danger!? Tsuchinoko?': 7,
      'Dracotail Mululu': 7,
      'Fire King High Avatar Kirin': 7,
      'Flying "C"': 7,
      'Mathmech Sigma': 7,
      'Mulcharmy Fuwalos': 7,
      'Noh-P.U.N.K. Foxy Tune': 7,
      'PSY-Framegear Delta': 7,
      'PSY-Framegear Epsilon': 7,
      'Purrelyly': 7,
      'Radiant Typhoon Eldam': 7,
      'Regenesis Sage': 7,
      'Speedroid Terrortop': 7,
      "Elder Entity N'tss": 7,
      'Foolish Burial Goods': 7,
      'Into the Void': 7,
      'Left Arm Offering': 7,
      'Return of the Dragon Lords': 7,
      'Sky Striker Mobilize - Engage!': 7,
      'Toon Table of Contents': 7,
      'Upstart Goblin': 7,
      'Chicken Game': 7,
      'Exosister Betrayal': 7,
      'Goblin Biker Grand Breakout': 7,
      'Evenly Matched': 7,
      'Transaction Rollback': 7,
      'Trap Holic': 7,
      "Dragon's Mind": 7,
      'Solemn Judgment': 7,

      // ═══ 6 points ═══
      'Ghost Belle & Haunted Mansion': 6,
      'Kewl Tune Clip': 6,
      'Kewl Tune Cue': 6,
      'Nibiru, the Primal Being': 6,
      'Change of Heart': 6,
      'Heavy Storm': 6,

      // ═══ 5 points ═══
      'Blaster, Dragon Ruler of Infernos': 5,
      'Crystron Sulfador': 5,
      'Darklord Ixchel': 5,
      'Dinowrestler Pankratops': 5,
      'Ghost Mourner & Moonlit Chill': 5,
      'Grapha, Dragon Lord of Dark World': 5,
      'Heart of the Blue-Eyes': 5,
      'Ichiki Sayori-Hime': 5,
      'Lyrilusc - Beryl Canary': 5,
      'Medius the Pure': 5,
      'Redox, Dragon Ruler of Boulders': 5,
      'Rescue-ACE Air Lifter': 5,
      'Rescue-ACE Impulse': 5,
      'Retaliating "C"': 5,
      'Silvy of the White Forest': 5,
      'Star Seraph Scepter': 5,
      'Star Seraph Sovereignty': 5,
      'Subterror Guru': 5,
      'Swordsoul Strategist Longyuan': 5,
      'Tempest, Dragon Ruler of Storms': 5,
      'Tidal, Dragon Ruler of Waterfalls': 5,
      'Tri-Brigade Mercourier': 5,
      'Albion the Branded Dragon': 5,
      'Grapha, Dragon Overlord of Dark World': 5,
      'Nerva the Power Patron of Creation': 5,
      'The Dragon that Devours the Dogma': 5,
      'Allure of Darkness': 5,
      'Celestial Observatory': 5,
      'Dark World Dealings': 5,
      'Metamorphosis': 5,
      'Preparation of Rites': 5,
      'Rank-Up-Magic Argent Chaos Force': 5,
      'Rank-Up-Magic Skip Force': 5,
      'Rank-Up-Magic Soul Shave Force': 5,
      'Theorealize': 5,
      'Trade-In': 5,
      'Stand Up Centur-Ion!': 5,
      'Stray Purrely Street': 5,
      'The Gates of Dark World': 5,
      'Treasures of the Kings': 5,
      'Dark World Archives': 5,
      'Exosister Pax': 5,
      'Forbidden Chalice': 5,
      'Dragonmaid Tidying': 5,
      'Iron Thunder': 5,
      'Solemn Scolding': 5,
      'Solemn Strike': 5,
      'Solemn Warning': 5,
      "K9-ØØ Lupis": 5,

      // ═══ 4 points ═══
      'Ghost Ogre & Snow Rabbit': 4,
      'Garura, Wings of Resonant Life': 4,
      'Golden Cloud Beast - Malong': 4,
      'Mereologic Aggregator': 4,
      'Monster Reborn': 4,
      'Raigeki': 4,
      'Book of Moon': 4,
      'Snatch Steal': 4,

      // ═══ 3 points ═══
      'Astral Kuriboh': 3,
      'Blazing Cartesia, the Virtuous': 3,
      'Broww, Huntsman of Dark World': 3,
      'Danger! Bigfoot!': 3,
      'Danger! Chupacabra!': 3,
      'Danger! Dogman!': 3,
      'Danger! Mothman!': 3,
      'Danger! Ogopogo!': 3,
      'Danger! Thunderbird!': 3,
      'Dogmatika Ecclesia, the Virtuous': 3,
      'Ghost Sister & Spooky Dogwood': 3,
      'Guiding Quem, the Virtuous': 3,
      'Incredible Ecclesia, the Virtuous': 3,
      'Knight Armed Dragon, the Armored Knight Dragon': 3,
      "Koa'ki Meiru Guardian": 3,
      "Koa'ki Meiru Overload": 3,
      "Koa'ki Meiru Sandman": 3,
      "Koa'ki Meiru Wall": 3,
      'Noh-P.U.N.K. Rising Scale': 3,
      'Planet Pathfinder': 3,
      'Regenesis Warrior': 3,
      'Steel-Stringed Sacrifice': 3,
      'Tenyi Spirit - Ashuna': 3,
      'Tour Guide From the Underworld': 3,
      'Virtual World Mai-Hime - Lulu': 3,
      'Sauravis, the Ancient and Ascended': 3,
      'Ecclesia and the Dark Dragon': 3,
      'Artmage Varnish -Alteration-': 3,
      'Millennium Ankh': 3,
      'Sandwich Day': 3,
      'Spell Card "Soul Exchange"': 3,
      'Multi-Universe': 3,
      'Pseudo Space': 3,
      'Artmage Vandalism -Assault-': 3,
      'Book of Eclipse': 3,
      "Dragon's Light and Darkness": 3,
      'Duality': 3,
      'Forbidden Lance': 3,
      'Premature Burial': 3,
      'Dracotail Flame': 3,
      'Metaverse': 3,
      'Ojama Trio': 3,
      'Trap Trick': 3,

      // ═══ 2 points ═══
      'Centur-Ion Primera': 2,
      'Mulcharmy Meowls': 2,
      'Dark Ruler No More': 2,
      'Ojama Duo': 2,

      // ═══ 1 point ═══
      'Assault Synchron': 1,
      'Centur-Ion Trudea': 1,
      'D.D. Crow': 1,
      'Dark End Evaporation Dragon': 1,
      'Eva': 1,
      'Jet Synchron': 1,
      'Keldo the Sacred Protector': 1,
      'Light End Sublimation Dragon': 1,
      'Manju of the Ten Thousand Hands': 1,
      'Mind Master': 1,
      'Mudora the Sword Oracle': 1,
      'QQ Enneagon': 1,
      "Ra's Disciple": 1,
      'Senju of the Thousand Hands': 1,
      'Evigishki Mind Augus': 1,
      'Lyrilusc - Independent Nightingale': 1,
      'Supreme King Dragon Starving Venom': 1,
      'Black-Winged Assault Dragon': 1,
      'Bramble Rose Dragon': 1,
      'Daigusto Emeral': 1,
      'Laevatein, Generaider Boss of Shadows': 1,
      'Number 69: Heraldry Crest': 1,
      'Red-Eyes Flare Metal Dragon': 1,
      'Creature Swap': 1,
      'Dark Hole': 1,
      'Hyper Rank-Up-Magic Utopiforce': 1,
      'Rank-Up-Magic - The Seventh One': 1,
      'Rank-Up-Magic Admiration of the Thousands': 1,
      'Rank-Up-Magic Astral Force': 1,
      "Rank-Up-Magic Barian's Force": 1,
      "Rank-Up-Magic Limited Barian's Force": 1,
      'Rank-Up-Magic Numeron Force': 1,
      'Rank-Up-Magic Raid Force': 1,
      'Rank-Up-Magic Zexal Force': 1,
      'Ultimate Slayer': 1,
      'Light Barrier': 1,
      'Cosmic Cyclone': 1,
      'Demise of the Land': 1,
      'Gishki Aquamirror': 1,
      "Phantom Knights' Rank-Up-Magic Force": 1,
      'Rank-Up-Magic Cipher Ascension': 1,
      'Rank-Up-Magic Doom Double Force': 1,
      'Rank-Up-Magic Magical Force': 1,
      'Rank-Up-Magic Quick Chaos': 1,
      "Rank-Up-Magic Raptor's Force": 1,
      'Rank-Up-Magic Revolution Force': 1,
      "Rise Rank-Up-Magic Raidraptor's Force": 1,
      'The Gaze of Timaeus': 1,
      "The Phantom Knights' Rank-Up-Magic Launch": 1,
      'World Legacy Monstrosity': 1,
      'Butterfly Dagger - Elma': 1,
      'Gishki Nekromirror': 1,
      'Destructive Daruma Karma Cannon': 1,
      'Dinomorphia Domain': 1,
      'Dinomorphia Frenzy': 1,
      'Dinomorphia Intact': 1,
    },
  },
};

/**
 * Get available GENESYS versions.
 */
export const getGenesysVersions = () =>
  Object.entries(genesysBanlists).map(([id, v]) => ({ id, label: v.label, effectiveDate: v.effectiveDate }));

/**
 * Resolve GENESYS points for a card from a specific version.
 * Uses exact normalized name matching only.
 * Returns { points, found, normalizedKey }.
 */
export function resolveGenesysPoints(cardName, version = FORMAT_RULES.defaultVersion) {
  const banlist = genesysBanlists[version];
  if (!banlist) return { points: 0, found: false, normalizedKey: '', version };

  const normalizedKey = normalizeCardName(cardName);
  const cards = banlist.cards;

  // Exact match first
  if (cards[normalizedKey] !== undefined) {
    return { points: cards[normalizedKey], found: true, normalizedKey, version };
  }

  // Try normalized lookup against all keys
  for (const [key, pts] of Object.entries(cards)) {
    if (normalizeCardName(key) === normalizedKey) {
      return { points: pts, found: true, normalizedKey, version };
    }
  }

  return { points: 0, found: false, normalizedKey, version };
}

/**
 * Check if a card type string indicates a Link monster.
 */
export const isLinkType = (cardType) => {
  if (!cardType) return false;
  return FORMAT_RULES.linkKeywords.some(kw => cardType.includes(kw));
};

/**
 * Check if a card type string indicates a Pendulum monster.
 */
export const isPendulumType = (cardType) => {
  if (!cardType) return false;
  return FORMAT_RULES.pendulumKeywords.some(kw => cardType.includes(kw));
};
